export const normalizeIssue = (issueData) => ({
  id: issueData.iid,
  key: `${issueData.project_name}-${issueData.iid}`,
  description: issueData.description,
  title: issueData.title,
  projectName: issueData.project_name,
  reference: `#${issueData.iid}`,
  webUrl: issueData.webUrl,
  labels: issueData.labels || [],
  milestone: issueData.milestone,
  created_at: issueData.created_at,
  dueDate: issueData.dueDate,
  timeEstimate: issueData.timeEstimate,
  totalTimeSpent: issueData.totalTimeSpent,
  exceeded_time: issueData.exceeded_time,
  assign_epoch: issueData.assign_epoch,
});

export const normalizeUserIssuesResponse = (username, response) => {
  if (response?.error) {
    return { error: response.error };
  }

  let payload = { issues: [] };

  if (response?.record?.[username]) {
    payload = response.record[username];
  } else if (response?.record) {
    payload = response.record;
  } else if (response) {
    payload = response;
  }

  const issues = Array.isArray(payload.issues)
    ? payload.issues.map(normalizeIssue)
    : [];

  return {
    issues,
    error: false,
  };
};

export const computeLabelStats = (issues) => {
  const labelMap = {};

  issues.forEach((issue) => {
    issue.labels?.forEach((label) => {
      if (!label?.title) return;

      if (!labelMap[label.title]) {
        labelMap[label.title] = {
          name: label.title,
          count: 0,
          color: label.color,
        };
      }

      labelMap[label.title].count += 1;
    });
  });

  return Object.values(labelMap).sort(
    (a, b) => b.count - a.count || a.name.localeCompare(b.name)
  );
};

export const computeIssueStats = (issues) => {
  const now = Math.floor(Date.now() / 1000);

  const summary = issues.reduce(
    (stats, issue) => {
      stats.open += 1;

      if (issue.dueDate && issue.dueDate < now) {
        stats.overdue += 1;
      } else if (
        issue.dueDate &&
        issue.dueDate >= now &&
        issue.dueDate - now <= 3 * 24 * 60 * 60
      ) {
        stats.dueSoon += 1;
      }

      if (issue.exceeded_time && issue.exceeded_time > 0) {
        stats.exceeded += 1;
      }

      return stats;
    },
    { open: 0, overdue: 0, dueSoon: 0, exceeded: 0 }
  );

  return {
    ...summary,
    byLabel: computeLabelStats(issues),
  };
};

export const filterIssues = (issues, { search, label, milestone, status }) => {
  const query = search?.trim().toLowerCase();

  return issues.filter((issue) => {
    if (query) {
      const haystack = [
        issue.title,
        issue.description,
        issue.projectName,
        issue.reference,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (!haystack.includes(query)) return false;
    }

    if (label && label !== "all") {
      const hasLabel = issue.labels?.some((l) => l.title === label);
      if (!hasLabel) return false;
    }

    if (milestone && milestone !== "all") {
      if (issue.milestone?.title !== milestone) return false;
    }

    if (status && status !== "all") {
      const now = Math.floor(Date.now() / 1000);
      if (status === "overdue" && (!issue.dueDate || issue.dueDate >= now)) {
        return false;
      }
      if (
        status === "dueSoon" &&
        (!issue.dueDate ||
          issue.dueDate < now ||
          issue.dueDate - now > 3 * 24 * 60 * 60)
      ) {
        return false;
      }
      if (
        status === "exceeded" &&
        (!issue.exceeded_time || issue.exceeded_time <= 0)
      ) {
        return false;
      }
    }

    return true;
  });
};

export const collectFilterOptions = (issues) => {
  const labels = new Set();
  const milestones = new Set();

  issues.forEach((issue) => {
    issue.labels?.forEach((label) => labels.add(label.title));
    if (issue.milestone?.title) milestones.add(issue.milestone.title);
  });

  return {
    labels: Array.from(labels).sort(),
    milestones: Array.from(milestones).sort(),
  };
};
